import { PaginationResult, SingletonResult } from 'gql/Common';
import { LITE_REQUEST_ACCESS } from 'gql/TargetGroup';
import Helper from 'utils/Helper';
import BaseTarget from './BaseTarget';
import { IDed } from './Request';

export interface MsEntraID extends BaseTarget {
  tenantID: string;
}

export const LITE_MS_ENTRA_ID = `
{
  id
  name
  description
  tier
  createdBy
  createdAt
  updatedBy
  updatedAt
  deleted
}`;

export const FULL_MS_ENTRA_ID = `
{
  id
  createdBy
  createdAt
  updatedBy
  updatedAt
  name
  description
  tier
  tags {
    key
    value
  }

  approvalSlackChannel
  notifyAutoApproval
  enablePaging

  teamRole
  managersADGroups
  orgLeaderRole
  limitedAccessRoles
  accessRequiresBastion
  accessConfiguration {
    level
    access ${LITE_REQUEST_ACCESS}
  }
  deleted

  tenantID
}
`;

export interface ListMsEntraIDsFilterInput {
  includeDeleted: boolean;
}

export interface MsEntraIDResult {
  item: MsEntraID;
}

export interface ListMsEntraIDOptions {
  includeDeleted?: boolean;
  size?: number;
  nextToken?: string;
}

const LIST_MS_ENTRA_ID = `
  query listMsEntraIDs($filter: ListMsEntraIDsFilterInput, $pagination: PaginationInput) {
    listMsEntraIDs (filter: $filter, pagination: $pagination) {
      items ${LITE_MS_ENTRA_ID}
      nextToken
      total
    }
  }
`;

export const listMsEntraID = async (options?: ListMsEntraIDOptions): Promise<PaginationResult<MsEntraID>> => {
  const result = await Helper.execGQL<PaginationResult<MsEntraID>>({
    query: LIST_MS_ENTRA_ID,
    variables: {
      filter: {
        includeDeleted: options?.includeDeleted,
      },
      pagination: {
        size: options?.size,
        nextToken: options?.nextToken,
      },
    },
  });
  return result.listMsEntraIDs;
};

export interface GetMsEntraIDOptions {
  id: string;
}

const GET_MS_ENTRA_ID = `
  query getMsEntraID($id: String!) {
    getMsEntraID(id: $id) {
      item ${FULL_MS_ENTRA_ID}
    }
  }`;

export const getMsEntraID = async (options: GetMsEntraIDOptions): Promise<MsEntraID> => {
  const result = await Helper.execGQL<SingletonResult<MsEntraID>>({
    query: GET_MS_ENTRA_ID,
    variables: {
      id: options.id,
    },
  });
  return result.getMsEntraID.item;
};

const CREATE_MS_ENTRA_ID = `
  mutation createMsEntraID($create: CreateMsEntraIDInput!) {
    createMsEntraID(create: $create) {
      item {
        id
      }
    } 
  }`;

export const createMsEntraID = async (entraID: MsEntraID): Promise<IDed> => {
  const result = await Helper.execGQL<SingletonResult<MsEntraID>>({
    query: CREATE_MS_ENTRA_ID,
    variables: {
      create: entraID,
    },
  });
  return result.createMsEntraID.item;
};

const UPDATE_MS_ENTRA_ID = `
  mutation updateMsEntraID($update: UpdateMsEntraIDInput!) {
    updateMsEntraID(update: $update) {
      item { 
        id
      }
    }
  }`;

export const updateMsEntraID = async (entraID: MsEntraID): Promise<IDed> => {
  const result = await Helper.execGQL<SingletonResult<MsEntraID>>({
    query: UPDATE_MS_ENTRA_ID,
    variables: {
      update: entraID,
    },
  });
  return result.updateMsEntraID.item;
};

const DELETE_MS_ENTRA_ID = `
mutation deleteMsEntraID($id: String!) {
  deleteMsEntraID(id: $id) {
      item {
          id
      }
  }
} `;

export const deleteMsEntraID = async (id: string): Promise<IDed> => {
  const result = await Helper.execGQL<SingletonResult<MsEntraID>>({
    query: DELETE_MS_ENTRA_ID,
    variables: {
      id,
    },
  });
  return result.deleteMsEntraID.item;
};
